import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';

export type ChatMode = 'chat' | 'code' | 'system';
export type ChatSource = 'claude_chat' | 'claude_code' | 'system';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  source: ChatSource;
  thinking?: string;
  timestamp: Date;
  isLoading?: boolean;
}

export interface ApprovalRequest {
  id: string;
  type: 'deployment' | 'config_change' | 'user_action' | 'code_execution';
  source: ChatSource;
  summary: string;
  details: string;
  riskLevel: 'low' | 'medium' | 'high';
  expiresAt?: Date;
  status: 'pending' | 'approved' | 'rejected';
}

interface ChatContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggleChat: () => void;
  mode: ChatMode;
  setMode: (mode: ChatMode) => void;
  messages: ChatMessage[];
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  clearMessages: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  showThinking: boolean;
  setShowThinking: (show: boolean) => void;
  approvalRequests: ApprovalRequest[];
  addApprovalRequest: (request: Omit<ApprovalRequest, 'id' | 'status'>) => void;
  handleApproval: (id: string, approved: boolean, reason?: string) => void;
  selectedModel: string;
  setSelectedModel: (model: string) => void;
  sendMessage: (content: string) => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: ReactNode }) {
  const { authFetch } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<ChatMode>('chat');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showThinking, setShowThinking] = useState(true);
  const [approvalRequests, setApprovalRequests] = useState<ApprovalRequest[]>([]);
  const [selectedModel, setSelectedModel] = useState('claude-opus-4-5-20251101');

  const toggleChat = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const addMessage = useCallback((message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const newMessage: ChatMessage = {
      ...message,
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage.id;
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const addApprovalRequest = useCallback((request: Omit<ApprovalRequest, 'id' | 'status'>) => {
    const newRequest: ApprovalRequest = {
      ...request,
      id: `approval-${Date.now()}`,
      status: 'pending',
    };
    setApprovalRequests(prev => [...prev, newRequest]);
  }, []);

  const handleApproval = useCallback((id: string, approved: boolean, reason?: string) => {
    setApprovalRequests(prev =>
      prev.map(req =>
        req.id === id
          ? { ...req, status: approved ? 'approved' : 'rejected' }
          : req
      )
    );

    // Add system message about approval
    addMessage({
      role: 'system',
      content: approved
        ? `Request approved${reason ? `: ${reason}` : ''}`
        : `Request rejected${reason ? `: ${reason}` : ''}`,
      source: 'system',
    });
  }, [addMessage]);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    const source: ChatSource = 'claude_chat';

    // Add user message
    addMessage({
      role: 'user',
      content,
      source,
    });

    setIsLoading(true);

    try {
      // Call the chat API with authentication
      const response = await authFetch('/api/agent/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: content,
          role: 'general',
          stream_thinking: true,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Format thinking steps if present
        let thinkingText = '';
        if (data.thinking && Array.isArray(data.thinking) && data.thinking.length > 0) {
          thinkingText = data.thinking
            .map((step: { agent?: string; type?: string; thought?: string }) => 
              `[${step.agent || 'AI'}] ${step.thought || ''}`
            )
            .join('\n');
        }

        addMessage({
          role: 'assistant',
          content: data.response || 'No response generated',
          source,
          thinking: thinkingText || undefined,
        });
      } else {
        // Try to get error details
        let errorMessage = 'Failed to get response from AI';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch {
          // If we can't parse the error, use status text
          errorMessage = `Error ${response.status}: ${response.statusText}`;
        }
        
        addMessage({
          role: 'system',
          content: errorMessage,
          source: 'system',
        });
      }
    } catch (error) {
      console.error('Chat error:', error);
      addMessage({
        role: 'system',
        content: error instanceof Error ? error.message : 'Error connecting to AI service',
        source: 'system',
      });
    } finally {
      setIsLoading(false);
    }
  }, [authFetch, addMessage]);

  const value: ChatContextType = {
    isOpen,
    setIsOpen,
    toggleChat,
    mode,
    setMode,
    messages,
    addMessage,
    clearMessages,
    isLoading,
    setIsLoading,
    showThinking,
    setShowThinking,
    approvalRequests,
    addApprovalRequest,
    handleApproval,
    selectedModel,
    setSelectedModel,
    sendMessage,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}

export default ChatContext;
